/**
 * Second pass over a finished take: replays the recorded video into an
 * offscreen canvas, paints the note effects on top, and re-records the result
 * together with the original audio.
 *
 * Everything it allocates (object URL, detached <video>, AudioContext,
 * MediaRecorder) is registered with a resource scope, so an early bail-out or
 * a thrown error releases it all. That matters because browsers cap the number
 * of live AudioContexts: leaking one per failed export is enough to break
 * exporting for the rest of the session.
 */
import { createFrameLoop } from "./frameLoop";
import { createResourceScope } from "./resourceScope";
import { computeGlowIntensity, computeParticlesAt } from "./effectsTimeline";
import {
  activeNotesAt,
  closeOpenNotes,
  computeProgressPercent,
  resolveMediaDurationMs,
  selectSupportedMimeType,
  sortNoteEvents,
} from "../helpers/recordingHelpers";
import type { NoteEvent } from "../helpers/recordingHelpers";
import {
  MASTER_CANVAS_HEIGHT,
  MASTER_CANVAS_WIDTH,
  buildNoteToXMap,
  computePianoTopY,
  hexToRgba,
} from "../helpers/pianoLayoutHelpers";
import type { PianoConfig } from "../helpers/pianoLayoutHelpers";

export const EFFECTS_CODEC_PREFERENCES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

/** Give up on a playback that has not advanced for this long. */
const STALL_TIMEOUT_MS = 2000;
/** Extra time granted after the expected duration before force-stopping. */
const OVERRUN_GRACE_MS = 1500;
/** Let the recorder capture the final frame before closing it. */
const FINAL_FRAME_FLUSH_MS = 300;

export interface RenderEffectsParams {
  recordedChunks: BlobPart[];
  /** Wall-clock length of the take; a MediaRecorder WebM carries no duration. */
  recordedDurationMs: number;
  noteEvents: readonly NoteEvent[];
  config: PianoConfig;
  fps: number;
  videoBitsPerSecond: number;
  onProgress?: (percent: number) => void;
}

export const renderEffectsVideo = async ({
  recordedChunks,
  recordedDurationMs,
  noteEvents,
  config,
  fps,
  videoBitsPerSecond,
  onProgress,
}: RenderEffectsParams): Promise<Blob> => {
  if (recordedChunks.length === 0) {
    throw new Error("There is nothing recorded to render.");
  }

  const scope = createResourceScope();

  try {
    const noteToX = buildNoteToXMap(config);
    const pianoTopY = computePianoTopY(config.userPianoHeight);
    const keyColor = config.pressedKeyColor;

    // Anything still held when the recorder stopped has to be released, or the
    // key stays lit for the remainder of the rendered video.
    const timeline = closeOpenNotes(
      sortNoteEvents(noteEvents),
      recordedDurationMs,
    );

    /* ── Source video ───────────────────────────────────────────────────── */

    const sourceBlob = new Blob(recordedChunks, { type: "video/webm" });
    const videoUrl = scope.add(URL.createObjectURL(sourceBlob), URL.revokeObjectURL);

    const sourceVideo = scope.add(document.createElement("video"), (element) => {
      element.pause();
      element.removeAttribute("src");
      element.load();
      element.remove();
    });
    sourceVideo.src = videoUrl;
    sourceVideo.muted = false; // createMediaElementSource needs the audio
    sourceVideo.playsInline = true;
    sourceVideo.style.display = "none";
    document.body.appendChild(sourceVideo);

    await new Promise<void>((resolve, reject) => {
      sourceVideo.onloadeddata = () => resolve();
      sourceVideo.onerror = () => reject(new Error("Could not read the recorded video."));
      sourceVideo.load();
    });

    const durationMs = resolveMediaDurationMs(
      sourceVideo.duration,
      recordedDurationMs,
    );

    /* ── Audio passthrough ──────────────────────────────────────────────── */

    const audioContext = scope.add(new AudioContext(), (context) => {
      if (context.state !== "closed") void context.close();
    });
    // An AudioContext can start suspended under autoplay policy, which would
    // silently produce a video with no sound.
    if (audioContext.state === "suspended") {
      await audioContext.resume().catch(() => {});
    }
    const audioDestination = audioContext.createMediaStreamDestination();
    audioContext.createMediaElementSource(sourceVideo).connect(audioDestination);
    // Deliberately not connected to audioContext.destination — no speakers.

    /* ── Output canvas and recorder ─────────────────────────────────────── */

    const effectsCanvas = document.createElement("canvas");
    effectsCanvas.width = MASTER_CANVAS_WIDTH;
    effectsCanvas.height = MASTER_CANVAS_HEIGHT;
    const ctx = effectsCanvas.getContext("2d");
    if (!ctx) throw new Error("Could not create the rendering canvas.");

    const effectsStream = scope.add(effectsCanvas.captureStream(fps), (stream) =>
      stream.getTracks().forEach((track) => track.stop()),
    );
    const audioTrack = audioDestination.stream.getAudioTracks()[0];
    if (audioTrack) effectsStream.addTrack(audioTrack);

    const mimeType = selectSupportedMimeType(EFFECTS_CODEC_PREFERENCES, (type) =>
      MediaRecorder.isTypeSupported(type),
    );
    const recorder = new MediaRecorder(effectsStream, {
      ...(mimeType ? { mimeType } : {}),
      videoBitsPerSecond,
    });

    const chunks: BlobPart[] = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };

    /* ── Render ─────────────────────────────────────────────────────────── */

    const drawFrame = () => {
      const currentTimeMs = sourceVideo.currentTime * 1000;
      onProgress?.(computeProgressPercent(currentTimeMs, durationMs));

      ctx.drawImage(sourceVideo, 0, 0, MASTER_CANVAS_WIDTH, MASTER_CANVAS_HEIGHT);

      const activeNotes = activeNotesAt(timeline, currentTimeMs);
      const glow = computeGlowIntensity(activeNotes.size);

      const gradient = ctx.createLinearGradient(0, pianoTopY - 3, 0, pianoTopY + 3);
      gradient.addColorStop(0, hexToRgba(keyColor, 0));
      gradient.addColorStop(0.5, hexToRgba(keyColor, glow.alpha));
      gradient.addColorStop(1, hexToRgba(keyColor, 0));
      ctx.save();
      ctx.shadowBlur = glow.blur;
      ctx.shadowColor = keyColor;
      ctx.fillStyle = gradient;
      ctx.fillRect(0, pianoTopY - 3, MASTER_CANVAS_WIDTH, 6);
      ctx.restore();

      for (const particle of computeParticlesAt(timeline, currentTimeMs, {
        pianoTopY,
        whiteKeyWidth: config.whiteKeyWidth,
        noteToX,
      })) {
        ctx.save();
        ctx.globalAlpha = particle.opacity;
        ctx.shadowBlur = 12;
        ctx.shadowColor = keyColor;
        ctx.fillStyle = keyColor;
        ctx.translate(particle.x, particle.y);
        ctx.rotate(Math.PI / 4);
        ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
        ctx.fillStyle = "rgba(255,255,255,0.6)";
        const inner = particle.size * 0.35;
        ctx.fillRect(-inner / 2, -inner / 2, inner, inner);
        ctx.restore();
      }
    };

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      let lastTime = -1;
      let lastProgressAt = performance.now();
      const startedAt = performance.now();

      const finishRecording = () => {
        if (settled) return;
        settled = true;
        loop.stop();
        // One last beat so the recorder picks up the final painted frame.
        window.setTimeout(() => {
          recorder.onstop = () => resolve();
          if (recorder.state !== "inactive") {
            recorder.stop();
          } else {
            resolve();
          }
        }, FINAL_FRAME_FLUSH_MS);
      };

      const loop = createFrameLoop({
        onFrame: () => {
          drawFrame();

          const now = performance.now();
          if (sourceVideo.currentTime !== lastTime) {
            lastTime = sourceVideo.currentTime;
            lastProgressAt = now;
          }

          const ended = sourceVideo.ended;
          // Playback can wedge without ever firing `ended`; a paused-and-not-
          // advancing video used to end the render on its very first frame.
          const stalled = now - lastProgressAt > STALL_TIMEOUT_MS;
          const overran =
            durationMs > 0 && now - startedAt > durationMs + OVERRUN_GRACE_MS;

          if (ended || stalled || overran) finishRecording();
        },
        onError: (error, consecutiveErrors) => {
          console.warn("Effects frame failed:", error);
          // A single bad frame is survivable; a permanently broken canvas is not.
          if (consecutiveErrors >= 30) {
            settled = true;
            loop.stop();
            reject(error instanceof Error ? error : new Error(String(error)));
          }
        },
        hiddenFps: fps,
      });

      scope.defer(() => loop.stop());

      recorder.start(500);
      sourceVideo
        .play()
        .catch((error) => console.warn("Effects playback could not start:", error));
      loop.start();
    });

    onProgress?.(100);
    return new Blob(chunks, { type: mimeType ?? "video/webm" });
  } finally {
    scope.dispose();
  }
};
